/**
 * CENTRALIZED FORMATTING UTILITIES
 * =================================
 * All date, time, currency formatting functions
 * Reads settings from localStorage (synced from system settings)
 * 
 * USAGE: Import and use these functions EVERYWHERE
 * import { formatDate, formatTime, formatCurrency } from '../utils/formatUtils';
 */

// ============================================================================
// SETTINGS HELPER
// ============================================================================

/**
 * Get office settings from localStorage
 * Settings are synced by CompanyContext/SettingsContext on login
 */
export const getOfficeSettings = () => {
  try {
    // Try multiple possible storage keys
    const keys = ['officeSettings', 'companySettings', 'settings', 'dateSettings'];
    for (const key of keys) {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          timezone: parsed.timezone || 'Asia/Kolkata',
          dateFormat: parsed.date_format || parsed.dateFormat || 'DD/MM/YYYY',
          timeFormat: parsed.time_format || parsed.timeFormat || '12h',
          currency: parsed.currency || 'INR',
          fiscalYearStart: parsed.fiscal_year_start || parsed.fiscalYearStart || '04-01',
          locale: getLocaleFromTimezone(parsed.timezone)
        };
      }
    }
  } catch (e) {
    console.warn('Failed to get office settings:', e);
  }
  
  // Default Indian settings
  return {
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    currency: 'INR',
    fiscalYearStart: '04-01',
    locale: 'en-IN'
  };
};

/**
 * Get locale code from timezone
 */
const getLocaleFromTimezone = (timezone) => {
  const timezoneToLocale = {
    'Asia/Kolkata': 'en-IN',
    'Europe/London': 'en-GB',
    'America/New_York': 'en-US',
    'America/Los_Angeles': 'en-US',
    'Europe/Paris': 'fr-FR',
    'Europe/Berlin': 'de-DE',
    'Asia/Dubai': 'ar-AE',
    'Asia/Singapore': 'en-SG',
    'Australia/Sydney': 'en-AU'
  };
  return timezoneToLocale[timezone] || 'en-IN';
};

/**
 * Get currency symbol from currency code
 */
const getCurrencySymbol = (currency) => {
  const symbols = {
    'INR': '₹',
    'USD': '$',
    'GBP': '£',
    'EUR': '€',
    'AED': 'د.إ',
    'SGD': 'S$',
    'AUD': 'A$'
  };
  return symbols[currency] || '₹';
};

// ============================================================================
// DATE FORMATTING
// ============================================================================

/**
 * Parse date string safely (no timezone shift)
 * ALWAYS use this instead of new Date(string)
 */
/**
 * Parse date string safely (no timezone shift)
 * ALWAYS use this instead of new Date(string)
 * Handles: Date objects, strings, numbers, null/undefined
 */
export const parseDate = (dateInput) => {
  // Handle null/undefined
  if (dateInput === null || dateInput === undefined || dateInput === '') return null;
  
  // Handle Date objects directly
  if (dateInput instanceof Date) {
    return isNaN(dateInput.getTime()) ? null : dateInput;
  }
  
  // Handle numbers (timestamps)
  if (typeof dateInput === 'number') {
    const d = new Date(dateInput);
    return isNaN(d.getTime()) ? null : d;
  }
  
  // Handle objects with toISOString (like MySQL Date results)
  if (typeof dateInput === 'object' && dateInput !== null) {
    if (typeof dateInput.toISOString === 'function') {
      return dateInput;
    }
    // Try to extract from object
    return null;
  }
  
  // Convert to string for parsing
  const str = String(dateInput);
  
  // Empty string check
  if (!str || str.trim() === '' || str === 'null' || str === 'undefined') return null;

  // YYYY-MM-DD format - add time to prevent timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return new Date(str + 'T00:00:00');
  }

  // DD/MM/YYYY or DD-MM-YYYY (Indian format)
  if (/^\d{2}[\/\-]\d{2}[\/\-]\d{4}$/.test(str)) {
    const sep = str.includes('/') ? '/' : '-';
    const [day, month, year] = str.split(sep);
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // MM/DD/YYYY (US format)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [month, day, year] = str.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // ISO format or other - parse directly
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : parsed;
};
/**
 * Format date to YYYY-MM-DD (for API calls)
 * Use this when sending dates to backend
 */
export const formatDateForAPI = (date) => {
  const d = parseDate(date);
  if (!d) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Alias
export const formatLocalDate = formatDateForAPI;

/**
 * Format date for display (uses system settings)
 * This is the MAIN function for displaying dates
 */
export const formatDate = (date, customFormat = null) => {
  // Guard against invalid dates like '0000-00-00' or empty strings
  if (!date || date === '0000-00-00' || date === 'Invalid Date') return '-';
  const testDate = new Date(date);
  if (isNaN(testDate.getTime())) return '-';
  const d = parseDate(date);
  if (!d) return '-';
  
  const settings = getOfficeSettings();
  const format = customFormat || settings.dateFormat;
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD-MM-YYYY':
      return `${day}-${month}-${year}`;
    case 'DD/MM/YYYY':
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Format date as human-readable (e.g., "26 Jan 2026")
 */
export const formatDateHuman = (date) => {
  const d = parseDate(date);
  if (!d) return '-';
  
  const settings = getOfficeSettings();
  return d.toLocaleDateString(settings.locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Format date with weekday (e.g., "Monday, 26 Jan 2026")
 */
export const formatDateWithDay = (date) => {
  const d = parseDate(date);
  if (!d) return '-';
  
  const settings = getOfficeSettings();
  return d.toLocaleDateString(settings.locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Format date as relative (Today, Yesterday, etc.)
 */
export const formatDateRelative = (date) => {
  const d = parseDate(date);
  if (!d) return '-';
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const inputDate = new Date(d);
  inputDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.floor((today - inputDate) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays === -1) return 'Tomorrow';
  if (diffDays > 1 && diffDays <= 7) return `${diffDays} days ago`;
  if (diffDays < -1 && diffDays >= -7) return `In ${Math.abs(diffDays)} days`;
  
  return formatDateHuman(d);
};

// ============================================================================
// TIME FORMATTING
// ============================================================================

/**
 * Format time according to system settings (12h or 24h)
 */
export const formatTime = (date) => {
  const d = parseDate(date);
  if (!d) return '-';
  
  const settings = getOfficeSettings();
  const hour12 = settings.timeFormat === '12h';
  
  return d.toLocaleTimeString(settings.locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: hour12
  });
};

/**
 * Format time with seconds
 */
export const formatTimeWithSeconds = (date) => {
  const d = parseDate(date);
  if (!d) return '-';
  
  const settings = getOfficeSettings();
  const hour12 = settings.timeFormat === '12h';
  
  return d.toLocaleTimeString(settings.locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: hour12
  });
};

/**
 * Format datetime (date + time)
 */
export const formatDateTime = (date) => {
  const d = parseDate(date);
  if (!d) return '-';
  
  return `${formatDate(d)} ${formatTime(d)}`;
};

/**
 * Format datetime as human readable
 */
export const formatDateTimeHuman = (date) => {
  const d = parseDate(date);
  if (!d) return '-';
  
  return `${formatDateHuman(d)} at ${formatTime(d)}`;
};

// ============================================================================
// CURRENCY FORMATTING
// ============================================================================

/**
 * Format currency amount according to system settings
 */
export const formatCurrency = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined || amount === '') return '-';
  
  const num = parseFloat(amount);
  if (isNaN(num)) return '-';
  
  const settings = getOfficeSettings();
  const symbol = getCurrencySymbol(settings.currency);
  
  // Format number with locale
  const formatted = num.toLocaleString(settings.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return showSymbol ? `${symbol}${formatted}` : formatted;
};

/**
 * Format currency without decimals (for whole numbers)
 */
export const formatCurrencyWhole = (amount, showSymbol = true) => {
  if (amount === null || amount === undefined || amount === '') return '-';
  
  const num = parseFloat(amount);
  if (isNaN(num)) return '-';
  
  const settings = getOfficeSettings();
  const symbol = getCurrencySymbol(settings.currency);
  
  const formatted = Math.round(num).toLocaleString(settings.locale);
  
  return showSymbol ? `${symbol}${formatted}` : formatted;
};

/**
 * Format currency with currency code (e.g., "INR 1,00,000")
 */
export const formatCurrencyWithCode = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '-';
  
  const num = parseFloat(amount);
  if (isNaN(num)) return '-';
  
  const settings = getOfficeSettings();
  
  const formatted = num.toLocaleString(settings.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${settings.currency} ${formatted}`;
};

/**
 * Get currency symbol only
 */
export const getCurrencySymbolFromSettings = () => {
  const settings = getOfficeSettings();
  return getCurrencySymbol(settings.currency);
};

// ============================================================================
// FISCAL YEAR HELPERS
// ============================================================================

/**
 * Get current fiscal year based on settings
 */
export const getCurrentFiscalYear = () => {
  const settings = getOfficeSettings();
  const [startMonth] = settings.fiscalYearStart.split('-').map(Number);
  
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  
  if (currentMonth >= startMonth) {
    return { start: currentYear, end: currentYear + 1 };
  } else {
    return { start: currentYear - 1, end: currentYear };
  }
};

/**
 * Format fiscal year (e.g., "FY 2025-26")
 */
export const formatFiscalYear = (year = null) => {
  const fy = year ? { start: year, end: year + 1 } : getCurrentFiscalYear();
  return `FY ${fy.start}-${String(fy.end).slice(-2)}`;
};

/**
 * Get fiscal year start date
 */
export const getFiscalYearStart = (year = null) => {
  const settings = getOfficeSettings();
  const [month, day] = settings.fiscalYearStart.split('-');
  const fy = year || getCurrentFiscalYear().start;
  return `${fy}-${month}-${day}`;
};

/**
 * Get fiscal year end date
 */
export const getFiscalYearEnd = (year = null) => {
  const settings = getOfficeSettings();
  const [startMonth, startDay] = settings.fiscalYearStart.split('-').map(Number);
  const fy = year || getCurrentFiscalYear().end;
  
  // End date is one day before start of next FY
  const endDate = new Date(fy, startMonth - 1, startDay);
  endDate.setDate(endDate.getDate() - 1);
  
  return formatDateForAPI(endDate);
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get today's date as YYYY-MM-DD
 */
export const getTodayString = () => formatDateForAPI(new Date());

/**
 * Check if date is today
 */
export const isToday = (date) => formatDateForAPI(date) === getTodayString();

/**
 * Check if date is in past
 */
export const isPastDate = (date) => formatDateForAPI(date) < getTodayString();

/**
 * Check if date is in future
 */
export const isFutureDate = (date) => formatDateForAPI(date) > getTodayString();

/**
 * Check if date is Sunday
 */
export const isSunday = (date) => {
  const d = parseDate(date);
  return d ? d.getDay() === 0 : false;
};

/**
 * Check if date is 2nd Saturday
 */
export const isSecondSaturday = (date) => {
  const d = parseDate(date);
  if (!d || d.getDay() !== 6) return false;
  const dayOfMonth = d.getDate();
  return dayOfMonth >= 8 && dayOfMonth <= 14;
};

/**
 * Add days to date
 */
export const addDays = (date, days) => {
  const d = parseDate(date);
  if (!d) return null;
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Get difference in days
 */
export const daysDifference = (date1, date2) => {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);
  if (!d1 || !d2) return 0;
  return Math.ceil(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24));
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Settings
  getOfficeSettings,
  
  // Date
  parseDate,
  formatDate,
  formatDateForAPI,
  formatLocalDate,
  formatDateHuman,
  formatDateWithDay,
  formatDateRelative,
  
  // Time
  formatTime,
  formatTimeWithSeconds,
  formatDateTime,
  formatDateTimeHuman,
  
  // Currency
  formatCurrency,
  formatCurrencyWhole,
  formatCurrencyWithCode,
  getCurrencySymbolFromSettings,
  
  // Fiscal Year
  getCurrentFiscalYear,
  formatFiscalYear,
  getFiscalYearStart,
  getFiscalYearEnd,
  
  // Utilities
  getTodayString,
  isToday,
  isPastDate,
  isFutureDate,
  isSunday,
  isSecondSaturday,
  addDays,
  daysDifference
};
